import axios from "axios";
import { serverURL } from "@/utils/serverURL";
import type {
  CatalogEntry,
  CatalogKind,
  CreateInventoryInput,
  InventoryPatch,
  LibraryItemFilters,
  LibraryItemListResponse,
  ResolveResult,
  SortAxis,
  SortDir,
  Supplier,
} from "./inventory_types";

const client = axios.create({ withCredentials: true });

export async function listLibraryItems(args: {
  cursor?: string;
  sort?: SortAxis;
  dir?: SortDir;
  filters?: LibraryItemFilters;
}): Promise<LibraryItemListResponse> {
  const params: Record<string, string | string[] | boolean> = {};
  if (args.cursor) params.cursor = args.cursor;
  if (args.sort) params.sort = args.sort;
  if (args.dir) params.dir = args.dir;
  const f = args.filters ?? {};
  if (f.search) params.search = f.search;
  if (f.family?.length) params.family = f.family;
  if (f.standard?.length) params.standard = f.standard;
  if (f.claim?.length) params.claim = f.claim;
  if (f.form?.length) params.form = f.form;
  if (f.storage?.length) params.storage = f.storage;
  if (f.custom?.length) params.custom = f.custom;
  if (f.stock) params.stock = f.stock;
  if (f.missing_chroma) params.missing_chroma = true;
  const res = await client.get(serverURL`/me/inventory/library-items`, { params });
  return res.data;
}

export async function resolveLibraryItem(input: {
  identifiers?: Array<{ kind: string; value: string }>;
  code?: string;
}): Promise<ResolveResult> {
  const res = await client.post(serverURL`/me/inventory/library-items/resolve`, input);
  return res.data;
}

export async function createInventory(input: CreateInventoryInput): Promise<{
  inventory_id: string;
  library_item_id: string;
}> {
  const res = await client.post(serverURL`/me/inventory`, input);
  return res.data;
}

export async function patchInventory(id: string, patch: InventoryPatch): Promise<{ ok: true }> {
  const res = await client.patch(serverURL`/me/inventory/${id}`, patch);
  return res.data;
}

export async function archiveInventory(id: string): Promise<{ ok: true }> {
  const res = await client.post(serverURL`/me/inventory/${id}/archive`);
  return res.data;
}

export async function unarchiveInventory(id: string): Promise<{ ok: true }> {
  const res = await client.post(serverURL`/me/inventory/${id}/unarchive`);
  return res.data;
}

export async function addInventoryIdentifier(
  invId: string,
  body: { kind: string; value: string },
) {
  const res = await client.post(serverURL`/me/inventory/${invId}/identifiers`, body);
  return res.data as { identifier_id: string };
}

export async function removeInventoryIdentifier(invId: string, identifierId: string) {
  const res = await client.delete(
    serverURL`/me/inventory/${invId}/identifiers/${identifierId}`,
  );
  return res.data as { ok: true };
}

/** Add a custom-tag link to an inventory row. Server endpoint kept at /tags
 *  for URL stability; the underlying table is inventory_tag_custom. */
export async function addInventoryTag(
  invId: string,
  body: { tag_id: string } | { display_text: string },
) {
  const res = await client.post(serverURL`/me/inventory/${invId}/tags`, body);
  return res.data as { link_id: string | null; tag_id: string };
}

export async function removeInventoryTagLink(invId: string, linkId: string) {
  const res = await client.delete(serverURL`/me/inventory/${invId}/tags/${linkId}`);
  return res.data as { ok: true };
}

export async function setInventoryLocation(
  invId: string,
  body:
    | { location_id: string }
    | { display_text: string; storage_condition_id?: string },
) {
  const res = await client.post(serverURL`/me/inventory/${invId}/location`, body);
  return res.data as { location_id: string };
}

// ── M:N flavor family / standard / claim sub-resources ────────────────────

export async function addInventoryFlavorFamily(
  invId: string,
  flavorFamilyId: string,
) {
  const res = await client.post(
    serverURL`/me/inventory/${invId}/flavor-families`,
    { flavor_family_id: flavorFamilyId },
  );
  return res.data as { rel_id: string | null; flavor_family_id: string };
}

export async function removeInventoryFlavorFamily(invId: string, relId: string) {
  const res = await client.delete(
    serverURL`/me/inventory/${invId}/flavor-families/${relId}`,
  );
  return res.data as { ok: true };
}

export async function addInventoryStandard(
  invId: string,
  body: { standard_id: string } | { standard_raw: string },
) {
  const res = await client.post(
    serverURL`/me/inventory/${invId}/standards`,
    body,
  );
  return res.data as {
    rel_id: string | null;
    standard_id: string | null;
    standard_raw: string | null;
  };
}

export async function removeInventoryStandard(invId: string, relId: string) {
  const res = await client.delete(
    serverURL`/me/inventory/${invId}/standards/${relId}`,
  );
  return res.data as { ok: true };
}

export async function addInventoryClaim(
  invId: string,
  body: { claim_id: string } | { claim_raw: string },
) {
  const res = await client.post(
    serverURL`/me/inventory/${invId}/claims`,
    body,
  );
  return res.data as {
    rel_id: string | null;
    claim_id: string | null;
    claim_raw: string | null;
  };
}

export async function removeInventoryClaim(invId: string, relId: string) {
  const res = await client.delete(
    serverURL`/me/inventory/${invId}/claims/${relId}`,
  );
  return res.data as { ok: true };
}

export async function listCatalog(
  kind: CatalogKind,
  search?: string,
): Promise<{
  items: CatalogEntry[];
  raws?: { raw: string; uses: number }[];
}> {
  const params: Record<string, string> = {};
  if (search) params.search = search;
  const res = await client.get(serverURL`/me/inventory/catalogs/${kind}`, { params });
  return res.data;
}

/** Create a TAG-kind catalog row in the caller's scope (org for shared,
 *  user_profile for personal). Idempotent by lowercased display_text. */
export async function createCatalogFlavorFamily(body: {
  display_text: string;
  color?: string;
}): Promise<{ id: string; displayText: string; color: string | null }> {
  const res = await client.post(
    serverURL`/me/inventory/catalogs/flavor-family`,
    body,
  );
  return res.data;
}

export async function createCatalogLocation(body: {
  display_text: string;
  storage_condition_id?: string;
}): Promise<{
  id: string;
  displayText: string;
  storageConditionId: string | null;
}> {
  const res = await client.post(
    serverURL`/me/inventory/catalogs/location`,
    body,
  );
  return res.data;
}

export async function createCatalogCustomTag(body: {
  display_text: string;
}): Promise<{ id: string; displayText: string }> {
  const res = await client.post(
    serverURL`/me/inventory/catalogs/custom`,
    body,
  );
  return res.data;
}

export async function normalizeCatalog(
  kind: CatalogKind,
  rawValue: string,
): Promise<{ resolution: "matched" | "new"; id: string; slug: string; display_text: string }> {
  const res = await client.post(serverURL`/me/inventory/catalogs/${kind}/normalize`, {
    raw_value: rawValue,
  });
  return res.data;
}

export async function listSuppliers(search?: string): Promise<{ items: Supplier[] }> {
  const params: Record<string, string> = {};
  if (search) params.search = search;
  const res = await client.get(serverURL`/me/inventory/suppliers`, { params });
  return res.data;
}

export async function listSupplierCatalogNos(
  supplierId: number,
  search?: string,
): Promise<{ items: string[] }> {
  const params: Record<string, string> = {};
  if (search) params.search = search;
  const res = await client.get(
    serverURL`/me/inventory/suppliers/${supplierId}/catalog-nos`,
    { params },
  );
  return res.data;
}

export async function patchLibraryItem(id: string, patch: { code?: string; name?: string }) {
  const res = await client.patch(serverURL`/me/inventory/library-items/${id}`, patch);
  return res.data as { ok: true };
}

export async function regenerateLibraryItemName(id: string) {
  const res = await client.post(
    serverURL`/me/inventory/library-items/${id}/regenerate-name`,
  );
  return res.data as { name: string; status: "llm_generated" | "fallback" };
}

export async function presignInventoryDocument(
  invId: string,
  body: { filename: string; content_type: string; size_bytes: number },
) {
  const res = await client.post(
    serverURL`/me/inventory/${invId}/documents/presign`,
    body,
  );
  return res.data as {
    file_upload_id: string;
    presigned_url: string;
    s3_bucket: string;
    s3_key: string;
    expires_at: string;
    required_headers: Record<string, string>;
  };
}

export async function completeInventoryDocument(invId: string, fileUploadId: string) {
  const res = await client.post(
    serverURL`/me/inventory/${invId}/documents/${fileUploadId}/complete`,
  );
  return res.data as { inventory_document_id: string };
}

export async function archiveInventoryDocument(invId: string, docId: string) {
  const res = await client.delete(serverURL`/me/inventory/${invId}/documents/${docId}`);
  return res.data as { ok: true };
}

// CSV export — returns a URL string the UI can route a hidden <a> click through.
export function libraryItemsExportCsvUrl(filters?: LibraryItemFilters): string {
  const base = serverURL`/me/inventory/library-items/export.csv`;
  const url = new URL(base, window.location.origin);
  if (filters) {
    if (filters.search) url.searchParams.set("search", filters.search);
    for (const f of filters.family ?? []) url.searchParams.append("family", f);
    for (const s of filters.standard ?? []) url.searchParams.append("standard", s);
    for (const cl of filters.claim ?? []) url.searchParams.append("claim", cl);
    for (const fm of filters.form ?? []) url.searchParams.append("form", fm);
    for (const st of filters.storage ?? []) url.searchParams.append("storage", st);
    for (const c of filters.custom ?? []) url.searchParams.append("custom", c);
    if (filters.stock) url.searchParams.set("stock", filters.stock);
    if (filters.missing_chroma) url.searchParams.set("missing_chroma", "true");
  }
  return url.toString();
}
